import { useEffect, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, CreditCard, RefreshCw, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { PlanOverrideDialog } from '@/components/admin/PlanOverrideDialog';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { InlineLoader } from '@/components/ui/InlineLoader';
import { SectionTitle } from '@/components/ui/SectionTitle';
import { StatCard } from '@/components/ui/StatCard';
import { adminBillingService } from '@/services/admin-billing.service';
import { adminMonitoringService } from '@/services/admin-monitoring.service';
import { ApiError } from '@/services/http';
import { cn } from '@/lib/cn';
import type { MonitoringAccountRow, MonitoringOverview } from '@/types/api';

const PAGE_SIZE = 20;

/**
 * AdminMonitoringPage — `/admin/monitoring`.
 *
 * A read-only platform snapshot in four metric groups (Accounts, Engagement,
 * Kids activity, Revenue) over a searchable/paginated account roster. Numbers
 * come from two endpoints: `/admin/monitoring/overview` (the stat groups +
 * signups trend) and `/admin/monitoring/accounts` (the table).
 *
 * Active-user metrics (7/30-day) are intentionally absent — they need a
 * per-account last-login timestamp that doesn't exist yet; the Engagement
 * group shows a note in their place.
 */
export function AdminMonitoringPage() {
  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-8">
        <AdminSectionTabs />

        <div>
          <SectionTitle kicker="Admin · Monitoring" heading="Platform monitoring" />
          <p className="mt-1 text-[13px] text-or-slate-600">
            A live snapshot of accounts, engagement, kids activity, and revenue.
          </p>
        </div>

        <OverviewSections />
        <AccountsSection />
      </div>
    </AppShell>
  );
}

/** The four stat groups + the signups trend, from a single overview fetch. */
function OverviewSections() {
  const [data, setData] = useState<MonitoringOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminMonitoringService
      .overview()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load metrics.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <InlineLoader label="Loading metrics…" />;
  if (error) return <ErrorBox message={error} />;
  if (!data) return null;

  const { accounts, engagement, kids, revenue } = data;

  return (
    <div className="flex flex-col gap-8">
      <MetricGroup title="Accounts">
        <StatCard label="Total accounts" value={formatNumber(accounts.total)} />
        <StatCard
          label="Paid"
          value={formatNumber(accounts.paid)}
          tone="success"
          delta={`${formatNumber(accounts.paidPlus)} Plus · ${formatNumber(accounts.paidFamily)} Family`}
        />
        <StatCard
          label="Free"
          value={formatNumber(accounts.free)}
          delta={`${engagement.conversionRatePct}% converted to paid`}
        />
        <StatCard
          label="Total readers"
          value={formatNumber(accounts.totalReaders)}
          delta={`${accounts.avgReadersPerAccount} per account`}
        />
      </MetricGroup>

      <MetricGroup title="Engagement">
        <StatCard label="New signups · 7 days" value={formatNumber(accounts.newAccounts7d)} />
        <StatCard label="New signups · 30 days" value={formatNumber(accounts.newAccounts30d)} />
        <StatCard
          label="Conversion rate"
          value={`${engagement.conversionRatePct}%`}
          tone="success"
          delta="paid ÷ total accounts"
        />
      </MetricGroup>

      <SignupsTrend
        buckets={engagement.signupsByWeek}
        note={
          engagement.activeUsersAvailable
            ? undefined
            : 'Active users (7- and 30-day) unlock once per-account last-login tracking is added.'
        }
      />

      <MetricGroup title="Kids activity">
        <StatCard label="Quiz attempts" value={formatNumber(kids.quizAttempts)} />
        <StatCard
          label="Quiz pass rate"
          value={kids.quizPassRatePct === null ? '—' : `${kids.quizPassRatePct}%`}
          tone="success"
        />
        <StatCard label="Coins issued" value={formatNumber(kids.coinsIssued)} />
        <StatCard label="Coins spent" value={formatNumber(kids.coinsSpent)} />
        <StatCard label="Eggs hatched" value={formatNumber(kids.eggsHatched)} />
        <StatCard label="Creatures collected" value={formatNumber(kids.creaturesCollected)} />
        <StatCard label="Active reading goals" value={formatNumber(kids.activeReadingGoals)} />
      </MetricGroup>

      <MetricGroup title="Revenue">
        <StatCard
          label="Estimated MRR"
          tone="success"
          value={
            revenue.priceUnavailable && revenue.estimatedMrrCents === 0
              ? '—'
              : formatMoney(revenue.estimatedMrrCents)
          }
          delta={revenue.priceUnavailable ? 'Stripe unavailable' : 'after discounts · active subs'}
        />
        <StatCard label="Plus · active" value={formatNumber(revenue.plusActive)} />
        <StatCard label="Family · active" value={formatNumber(revenue.familyActive)} />
        <StatCard label="Trialing" value={formatNumber(revenue.trialing)} />
        <StatCard label="Past due" value={formatNumber(revenue.pastDue)} tone="warning" />
        <StatCard
          label="Canceling / canceled"
          value={formatNumber(revenue.cancelingOrCanceled)}
          tone="danger"
        />
      </MetricGroup>
    </div>
  );
}

/** Hand-rolled CSS bar chart — avoids pulling in a charting dependency. */
function SignupsTrend({
  buckets,
  note,
}: {
  buckets: MonitoringOverview['engagement']['signupsByWeek'];
  note?: string;
}) {
  const max = buckets.reduce((m, b) => Math.max(m, b.count), 0);
  const totalSignups = buckets.reduce((sum, b) => sum + b.count, 0);
  const first = buckets[0];
  const last = buckets[buckets.length - 1];

  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-[12px] font-semibold uppercase tracking-wide text-or-slate-500">
        New signups · last 12 weeks
      </h3>
      <div className="rounded-2xl border border-or-slate-200 bg-white p-5">
        {totalSignups === 0 ? (
          <p className="py-8 text-center text-[13px] text-or-slate-400">
            No new signups in the last 12 weeks.
          </p>
        ) : (
          <>
            <div className="flex h-32 items-end gap-1.5">
              {buckets.map((b) => (
                <div
                  key={b.weekStart}
                  className="flex h-full flex-1 items-end"
                  title={`Week of ${formatDate(b.weekStart)}: ${b.count} signup${b.count === 1 ? '' : 's'}`}
                >
                  <div
                    className="mx-auto w-full max-w-[26px] rounded-t bg-or-orange-400"
                    style={{ height: `${barHeightPct(b.count, max)}%` }}
                  />
                </div>
              ))}
            </div>
            {first && last ? (
              <div className="mt-2 flex justify-between text-[11px] text-or-slate-400">
                <span>{formatDate(first.weekStart)}</span>
                <span>{formatDate(last.weekStart)}</span>
              </div>
            ) : null}
          </>
        )}
      </div>
      {note ? <p className="text-[12px] text-or-slate-400">{note}</p> : null}
    </section>
  );
}

/** Paginated, searchable, filterable account roster. */
function AccountsSection() {
  const [rows, setRows] = useState<MonitoringAccountRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'paid' | 'free'>('all');
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [refreshTick, setRefreshTick] = useState(0);
  const [pendingDelete, setPendingDelete] = useState<MonitoringAccountRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [planEdit, setPlanEdit] = useState<MonitoringAccountRow | null>(null);
  // Per-row busy id rather than a page-wide flag, so one slow re-sync doesn't
  // disable every other row's actions.
  const [resyncingId, setResyncingId] = useState<string | null>(null);

  // Debounce the search box into the committed query, resetting to page 1.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchDraft.trim());
      setPage(1);
    }, 250);
    return () => clearTimeout(handle);
  }, [searchDraft]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminMonitoringService
      .accounts({
        take: PAGE_SIZE,
        skip: (page - 1) * PAGE_SIZE,
        search: search || undefined,
        filter,
      })
      .then((res) => {
        if (cancelled) return;
        setRows(res.items);
        setTotal(res.total);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load accounts.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, search, filter, refreshTick]);

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await adminMonitoringService.deleteAccount(pendingDelete.id);
      toast.success(`Deleted ${pendingDelete.firstName} ${pendingDelete.lastName}'s account.`);
      setPendingDelete(null);
      setRefreshTick((t) => t + 1);
    } catch (err) {
      // 409 = the account still has an active subscription that must be
      // canceled first; surface the server's message either way.
      toast.error(err instanceof ApiError ? err.message : 'Could not delete the account.');
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Re-read Stripe and re-mirror this account's billing columns. The recovery
   * lever for a row that diverged from Stripe — a dropped webhook, a lost
   * delivery race, an out-of-band dashboard edit.
   */
  const handleResync = async (row: MonitoringAccountRow) => {
    setResyncingId(row.id);
    try {
      const result = await adminBillingService.reconcile(row.id);
      if (result.note) {
        // The server declined to write (e.g. Stripe reports no subscription).
        toast.warning(result.note);
      } else if (result.changed) {
        toast.success(
          `${row.firstName} ${row.lastName}: ${result.before.planTier}/${result.before.subscriptionStatus ?? 'null'} → ` +
            `${result.after.planTier}/${result.after.subscriptionStatus ?? 'null'}`,
        );
      } else {
        toast.success('Already in sync with Stripe.');
      }
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not re-sync from Stripe.');
    } finally {
      setResyncingId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(total, page * PAGE_SIZE);

  return (
    <section className="flex flex-col gap-4">
      <div>
        <SectionTitle kicker="Admin · Monitoring" heading="Accounts" />
        <p className="mt-1 text-[13px] text-or-slate-600">
          Every account with its plan, subscription status, and reader count.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-xs">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-or-slate-400"
            aria-hidden
          />
          <input
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="Search by name…"
            className="w-full rounded-full border border-or-slate-200 bg-white py-2 pl-9 pr-3 text-[13px] text-or-slate-800 outline-none focus:border-or-slate-400"
          />
        </div>
        <div className="inline-flex items-center rounded-full border border-or-slate-200 bg-white p-1">
          {(['all', 'paid', 'free'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => {
                setFilter(f);
                setPage(1);
              }}
              className={cn(
                'rounded-full px-3.5 py-1.5 text-[12px] font-semibold capitalize transition-colors',
                filter === f
                  ? 'bg-or-slate-900 text-white'
                  : 'text-or-slate-600 hover:bg-or-slate-100',
              )}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <InlineLoader label="Loading accounts…" />
      ) : error ? (
        <ErrorBox message={error} />
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-or-slate-200 bg-white px-6 py-12 text-center text-[14px] text-or-slate-500">
          No accounts match this view.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-or-slate-200 bg-white">
          <table className="w-full text-left text-[13px]">
            <thead className="bg-or-slate-50 text-[11px] uppercase tracking-wide text-or-slate-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Account</th>
                <th className="px-4 py-3 font-semibold">Plan</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Readers</th>
                <th className="px-4 py-3 text-right font-semibold">Joined</th>
                <th className="px-4 py-3 text-right font-semibold">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-or-slate-100">
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="px-4 py-3 font-medium text-or-slate-800">
                    {row.firstName} {row.lastName}
                  </td>
                  <td className="px-4 py-3">
                    <Chip variant={planChipVariant(row)}>{planLabel(row.planTier)}</Chip>
                  </td>
                  <td className="px-4 py-3 text-or-slate-600">
                    {row.subscriptionStatus ?? '—'}
                    {row.cancelAtPeriodEnd ? (
                      <div className="text-[11px] text-amber-600">cancels at period end</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-right text-or-slate-600">{row.readerCount}</td>
                  <td className="px-4 py-3 text-right text-or-slate-500">
                    {formatDate(row.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleResync(row)}
                        disabled={resyncingId === row.id}
                        aria-label={`Re-sync ${row.firstName} ${row.lastName} from Stripe`}
                        title="Re-sync from Stripe"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-or-slate-400 transition hover:bg-or-blue-100 hover:text-or-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <RefreshCw
                          className={cn('h-4 w-4', resyncingId === row.id && 'animate-spin')}
                          aria-hidden
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => setPlanEdit(row)}
                        aria-label={`Change ${row.firstName} ${row.lastName}'s plan`}
                        title="Change plan"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-or-slate-400 transition hover:bg-or-orange-100 hover:text-or-orange-600"
                      >
                        <CreditCard className="h-4 w-4" aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingDelete(row)}
                        aria-label={`Delete ${row.firstName} ${row.lastName}'s account`}
                        title="Delete account"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-or-slate-400 transition hover:bg-or-rose-100 hover:text-or-rose-700"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-[12px] text-or-slate-500">
          {total === 0 ? 'No accounts' : `Showing ${from}–${to} of ${formatNumber(total)}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
            Prev
          </Button>
          <span className="text-[12px] text-or-slate-500">
            Page {page} / {totalPages}
          </span>
          <Button
            size="sm"
            variant="secondary"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="Delete this account?"
        description={
          pendingDelete
            ? `This permanently deletes ${pendingDelete.firstName} ${pendingDelete.lastName}'s account and all of its data (readers, bookmarks, reviews). This can't be undone. If the account has an active subscription, it must be canceled first.`
            : undefined
        }
        confirmLabel="Delete account"
        onConfirm={handleConfirmDelete}
        isConfirming={deleting}
      />

      <PlanOverrideDialog
        account={planEdit}
        onClose={() => setPlanEdit(null)}
        onSaved={() => setRefreshTick((t) => t + 1)}
      />
    </section>
  );
}

function MetricGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-[12px] font-semibold uppercase tracking-wide text-or-slate-500">
        {title}
      </h3>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{children}</div>
    </section>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-or-rose-200 bg-or-rose-50 px-4 py-3 text-[13px] text-or-rose-700">
      {message}
    </div>
  );
}

function planLabel(tier: string): string {
  if (tier === 'plus') return 'Plus';
  if (tier === 'family') return 'Family';
  if (tier === 'free') return 'Free';
  return tier;
}

function planChipVariant(row: MonitoringAccountRow): 'live' | 'blue' | 'default' {
  if (!row.isPaid) return 'default';
  return row.planTier === 'family' ? 'live' : 'blue';
}

function formatNumber(n: number): string {
  try {
    return new Intl.NumberFormat('en-US').format(n);
  } catch {
    return String(n);
  }
}

function formatMoney(cents: number): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function barHeightPct(count: number, max: number): number {
  if (max <= 0) return 0;
  return count > 0 ? Math.max((count / max) * 100, 6) : 0;
}
